import type { TranscriptEntry } from '@highlight/common'

/**
 * Transcript IPC contract.
 *
 * Live transcript data never goes through GlobalState (which fans every write
 * out to every renderer). Instead:
 *
 *  - The main process owns the buffers (TranscriptManager) and publishes
 *    revisioned deltas on {@link TRANSCRIPT_FEED_TOPIC} via the broadcast
 *    broker — only renderers that explicitly subscribed receive anything.
 *  - A renderer opening a transcript view fetches the full buffers once via
 *    the {@link TRANSCRIPT_SNAPSHOT_EVENT} invoke, then applies deltas.
 *    A revision gap means missed deltas → re-fetch the snapshot.
 *  - Partial (in-flight) lines stream on {@link TRANSCRIPT_PARTIALS_TOPIC},
 *    again only to subscribed renderers.
 *  - The audio-runtime renderer hands finalised entries to the main process
 *    with the targeted {@link TRANSCRIPT_ENTRY_EVENT} invoke instead of a
 *    store broadcast.
 *
 * Cheap presence booleans (TranscriptPresence) stay on GlobalState — they
 * only change on flips and are what non-transcript UI subscribes to.
 */

export const TRANSCRIPT_FEED_TOPIC = 'transcripts.feed'
export const TRANSCRIPT_PARTIALS_TOPIC = 'transcripts.partials'

/** GlobalEvents.invoke: renderer → main, returns {@link TranscriptFeedSnapshot}. */
export const TRANSCRIPT_SNAPSHOT_EVENT = 'transcripts:snapshot'
/** GlobalEvents.invoke: audio-runtime renderer → main, payload {@link TranscriptEntryPayload}. */
export const TRANSCRIPT_ENTRY_EVENT = 'transcripts:entry'

export type TranscriptBufferKey = 'processed' | 'provider' | 'merged'

export interface TranscriptFeedOp {
  kind: 'append' | 'replace'
  key: TranscriptBufferKey
  entries: Array<TranscriptEntry>
}

/** One flush from the main process. `revision` increments by exactly 1 per
 *  published delta so consumers can detect missed messages. */
export interface TranscriptFeedDelta {
  revision: number
  ops: Array<TranscriptFeedOp>
}

export interface TranscriptFeedSnapshot {
  revision: number
  processed: Array<TranscriptEntry>
  provider: Array<TranscriptEntry>
  merged: Array<TranscriptEntry>
}

/** Finalised entry pair handed from the audio-runtime renderer to main. */
export interface TranscriptEntryPayload {
  processed: TranscriptEntry
  provider: TranscriptEntry
}

export type TranscriptPartialSource = 'microphone' | 'loopback'

/**
 * Normalized partial (in-flight) transcript line. Every runtime maps its
 * native shape to this at the publisher, so consumers never see per-runtime
 * variants. `startTimestamp` is the absolute wall-clock start of the
 * in-flight segment — stable across updates to the same segment, which is
 * what consumers key collation/ordering on. `endTimestamp` is the latest
 * known end; the electron runtime only reports segment start, so it's
 * optional.
 */
export interface PartialTranscription {
  text: string
  startTimestamp: number
  endTimestamp?: number
}

export interface TranscriptPartialMessage {
  source: TranscriptPartialSource
  /** null clears the partial line for that source (a final landed / recording reset). */
  partial: PartialTranscription | null
}
